import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponsePaymentMethodCreate } from '../../../types/api/ApiResponsePaymentMethod';

interface AddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Phase = 'idle' | 'submitting' | 'redirecting';

/**
 * "Add a card" — explains the €0.00 card-verification checkout, then `POST /clients/payment-methods`
 * (no body) and redirects the browser to Mollie (docs/api-types/payment-methods.md). Mollie returns
 * the customer to `/payment/return?card={payment_method_id}`, which polls until the card is `valid`.
 */
const AddCardModal: React.FunctionComponent<AddCardModalProps> = ({ isOpen, onClose }) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPhase('idle');
      setError(null);
    }
  }, [isOpen]);

  const busy = phase !== 'idle';

  const handleContinue = useCallback(async () => {
    setPhase('submitting');
    setError(null);
    try {
      const response = await apiFetch('/clients/payment-methods', { method: 'POST' });
      // JSend: branch on body.status — a failure is `status: 'error'` at HTTP 200.
      const body = (await response.json().catch(() => null)) as ApiResponsePaymentMethodCreate | null;
      if (body?.status === 'success' && typeof body.data?.checkout_url === 'string' && body.data.checkout_url !== '') {
        // Hand off to Mollie; the page is unloading — keep the redirecting state.
        setPhase('redirecting');
        window.location.href = body.data.checkout_url;
        return;
      }
      const message = body?.message ?? 'Could not start the card registration. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not start card registration',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      setPhase('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected network error occurred.';
      setError(message);
      addNotification({
        title: 'Could not start card registration',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      setPhase('idle');
    }
  }, [apiFetch, addNotification, setDrawerOpen]);

  return (
    <Modal variant="small" isOpen={isOpen} onClose={busy ? undefined : onClose} aria-label="Add a card">
      <ModalHeader title="Add a card" />
      <ModalBody>
        {phase === 'redirecting' ? (
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Redirecting to the payment provider" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Redirecting you to our payment provider — please keep this window open.
              </Content>
            </FlexItem>
          </Flex>
        ) : (
          <>
            {error && <Alert variant="danger" isInline title={error} className="pf-v6-u-mb-md" />}
            <Content component="p">
              Your card is registered directly with our payment provider through a{' '}
              <strong>€0.00 card verification</strong> checkout — nothing is charged.
            </Content>
            <List className="pf-v6-u-mt-sm">
              <ListItem>
                The card number never reaches us: only its brand, last four digits, holder and expiry are kept.
              </ListItem>
              <ListItem>Once saved, it can be charged in one click for invoices, top-ups and domain orders.</ListItem>
              <ListItem>You can remove it at any time; removal is permanent at the payment provider.</ListItem>
            </List>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={() => void handleContinue()} isLoading={busy} isDisabled={busy}>
          Continue to card registration
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddCardModal;
